import FirmaPrueba from '../assets/FirmaPrueba.png';

/**
 * Servicio para manejar firmas locales en modo demo
 * Las firmas se almacenan solo en el localStorage del usuario actual
 */
export class DemoSignatureService {
  
  // Clave para almacenar firmas locales
  static DEMO_SIGNATURES_KEY = 'demoLocalSignatures';
  
  /**
   * Verificar si estamos en modo demo
   */
  static isDemoMode() {
    return sessionStorage.getItem('isDemoMode') === 'true';
  }
  
  /**
   * Marcar un reporte como firmado localmente
   * @param {string|number} reporteId - ID del reporte
   * @param {string} tipoFirma - 'supervisor' o 'inspector'
   * @returns {boolean} - true si se marcó exitosamente
   */
  static signReportLocally(reporteId, tipoFirma = 'supervisor') {
    if (!this.isDemoMode()) {
      return false;
    }
    
    try {
      const signatures = this.getLocalSignatures();
      const key = `${reporteId}_${tipoFirma}`;
      
      signatures[key] = {
        reporteId: reporteId.toString(),
        tipoFirma,
        firmadoEn: new Date().toISOString(),
        firmaBase64: FirmaPrueba // Usar la imagen importada
      };
      
      localStorage.setItem(this.DEMO_SIGNATURES_KEY, JSON.stringify(signatures));
      console.log(`🎬 Demo: Reporte ${reporteId} marcado como firmado localmente (${tipoFirma})`);
      
      return true;
    } catch (error) {
      console.error('Error al marcar firma local:', error);
      return false;
    }
  }
  
  /**
   * Verificar si un reporte está firmado localmente
   * @param {string|number} reporteId - ID del reporte
   * @param {string} tipoFirma - 'supervisor' o 'inspector'
   * @returns {boolean}
   */
  static isReportSignedLocally(reporteId, tipoFirma = 'supervisor') {
    if (!this.isDemoMode()) {
      return false;
    }
    
    const signatures = this.getLocalSignatures();
    const key = `${reporteId}_${tipoFirma}`;
    
    return signatures.hasOwnProperty(key);
  }
  
  /**
   * Obtener la firma local de un reporte
   * @param {string|number} reporteId - ID del reporte
   * @param {string} tipoFirma - 'supervisor' o 'inspector'
   * @returns {string|null} - Base64 de la firma o null si no existe
   */
  static getLocalSignature(reporteId, tipoFirma = 'supervisor') {
    if (!this.isDemoMode()) {
      return null;
    }
    
    const signatures = this.getLocalSignatures();
    const key = `${reporteId}_${tipoFirma}`;
    
    return signatures[key]?.firmaBase64 || null;
  }
  
  /**
   * Obtener todas las firmas locales
   * @returns {Object}
   */
  static getLocalSignatures() {
    try {
      const stored = localStorage.getItem(this.DEMO_SIGNATURES_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error al obtener firmas locales:', error);
      return {};
    }
  }
  
  /**
   * Limpiar todas las firmas locales
   */
  static clearLocalSignatures() {
    try {
      localStorage.removeItem(this.DEMO_SIGNATURES_KEY);
      console.log('🎬 Demo: Firmas locales limpiadas');
    } catch (error) {
      console.error('Error al limpiar firmas locales:', error);
    }
  }
  
  /**
   * Procesar un reporte para agregar firmas locales si existen
   * @param {Object} reporte - Objeto del reporte
   * @returns {Object} - Reporte con firmas locales aplicadas
   */
  static processReportWithLocalSignatures(reporte) {
    if (!this.isDemoMode() || !reporte?.idreporte) {
      return reporte;
    }
    
    const reporteProcessed = { ...reporte };
    
    // Verificar firma de supervisor local
    if (this.isReportSignedLocally(reporte.idreporte, 'supervisor')) {
      reporteProcessed.estadofirmasupervisor = true;
      
      // Si el reporte tiene una respuesta, agregar firma local
      if (reporteProcessed.respuesta) {
        reporteProcessed.respuesta = {
          ...reporteProcessed.respuesta,
          firmasupervisor: 'demo/firma-local-supervisor'
        };
      }
    }
    
    // Verificar firma de inspector local (si es necesario en el futuro)
    if (this.isReportSignedLocally(reporte.idreporte, 'inspector')) {
      reporteProcessed.estadofirmainspector = true;
      
      if (reporteProcessed.respuesta) {
        reporteProcessed.respuesta = {
          ...reporteProcessed.respuesta,
          firmainspector: 'demo/firma-local-inspector'
        };
      }
    }
    
    return reporteProcessed;
  }
  
  /**
   * Procesar una lista de reportes para agregar firmas locales
   * @param {Array} reportes - Lista de reportes
   * @returns {Array} - Lista de reportes con firmas locales aplicadas
   */
  static processReportsWithLocalSignatures(reportes) {
    if (!this.isDemoMode() || !Array.isArray(reportes)) {
      return reportes;
    }
    
    return reportes.map(reporte => this.processReportWithLocalSignatures(reporte));
  }
  
  /**
   * Verificar si una imagen es una firma local de demo
   * @param {string} imagePath - Ruta de la imagen
   * @returns {boolean}
   */
  static isLocalDemoSignature(imagePath) {
    return imagePath && imagePath.startsWith('demo/firma-local-');
  }
  
  /**
   * Obtener la imagen base64 para una firma local
   * @param {string} imagePath - Ruta de la imagen (demo/firma-local-supervisor o demo/firma-local-inspector)
   * @returns {string} - Base64 de la firma
   */
  static getLocalSignatureImage(imagePath) {
    if (this.isLocalDemoSignature(imagePath)) {
      return FirmaPrueba;
    }
    return null;
  }
}

export default DemoSignatureService;
